/**
 * 資訊主頁分析功能集中管理
 * 包含所有分析類型的 prompt 和處理函數
 */

import { message } from 'antd';
import { logger } from '../../utils/logger';
import { callAIProxyStream } from '../../api/aiProxy';
import { savePermanentCache } from '../../utils/cacheUtils';
import activityTracker from '../user/ActivityTracker';
import { convertMarkdownToHtml } from '../../shared/components/cc_html';

// 手動 Debug Flag - 設為 true 時會在 console 顯示完整的 HTML 分析內容
const DEBUG_MODE = false;

// 重新导出以保持向后兼容
export { convertMarkdownToHtml };

// 构建缓存 key
export const makeCacheKey = (analysisType, symbol) => {
  if (!analysisType || !symbol) return null;
  return `maininfo_${analysisType}_${symbol}`;
};

/**
 * 调用 Perplexity API 进行分析
 * @param {string} prompt - 分析提示词
 * @param {function} setResult - 设置结果的状态函数
 * @param {function} setLoadingState - 设置加载状态的函数
 * @param {string} analysisType - 分析类型
 * @param {string} symbol - 股票代码
 * @param {function} setIsAnalyzing - 设置总体分析状态的函数
 * @param {string} moduleName - 模块名称，用于获取对应的 API 配置
 */
export const callPerplexityAPI = async (
  prompt, 
  setResult, 
  setLoadingState, 
  analysisType, 
  symbol,
  setIsAnalyzing,
  moduleName = 'maininfo_other'
) => {
  try {
    // 检查是否使用 Deep Research 模型
    const isDeepResearch = moduleName === 'maininfo_fundamental';
    
    // 添加当前日期到结果开头
    const currentDate = new Date().toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    const dateHeader = `<div style="text-align: right; color: #666; font-size: 14px; margin-bottom: 20px;">分析日期：${currentDate}</div>\n\n`;
    
    // 如果是 Deep Research，显示特殊的加载 UI
    if (isDeepResearch) {
      const loadingUI = `
        <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 80px 40px; min-height: 500px; background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);">
          <!-- 主加载动画 -->
          <div style="position: relative; width: 120px; height: 120px; margin-bottom: 40px;">
            <div style="position: absolute; width: 100%; height: 100%; border: 8px solid transparent; border-top: 8px solid #1890ff; border-radius: 50%; animation: spin 1.2s linear infinite;"></div>
            <div style="position: absolute; width: 80%; height: 80%; top: 10%; left: 10%; border: 8px solid transparent; border-top: 8px solid #52c41a; border-radius: 50%; animation: spin 1.5s linear infinite reverse;"></div>
            <div style="position: absolute; width: 60%; height: 60%; top: 20%; left: 20%; border: 8px solid transparent; border-top: 8px solid #faad14; border-radius: 50%; animation: spin 1.8s linear infinite;"></div>
            <div style="position: absolute; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; font-size: 36px;">
              🧠
            </div>
          </div>
          
          <!-- 标题 -->
          <h2 style="color: #1890ff; font-size: 28px; font-weight: 700; margin: 0 0 16px 0; text-align: center; text-shadow: 2px 2px 4px rgba(0,0,0,0.1);">
            深度思考中
          </h2>
          
          <!-- 副标题 -->
          <h3 style="color: #52c41a; font-size: 18px; font-weight: 500; margin: 0 0 24px 0; text-align: center;">
             Deep Buffett AI Research
          </h3>
          
          <!-- 描述 -->
          <p style="color: #595959; font-size: 16px; margin: 0 0 30px 0; text-align: center; line-height: 1.8; max-width: 600px;">
            AI 正在深度搜尋多個權威來源<br/>
            並進行全面的數據分析與整理<br/>
            <span style="color: #ff4d4f; font-weight: 600;">請耐心等待，這需要較長時間...</span>
          </p>
          
          <!-- 进度点动画 -->
          <div style="display: flex; gap: 12px; margin-bottom: 30px;">
            <div style="width: 16px; height: 16px; background-color: #1890ff; border-radius: 50%; animation: pulse 1.4s ease-in-out infinite;"></div>
            <div style="width: 16px; height: 16px; background-color: #52c41a; border-radius: 50%; animation: pulse 1.4s ease-in-out 0.2s infinite;"></div>
            <div style="width: 16px; height: 16px; background-color: #faad14; border-radius: 50%; animation: pulse 1.4s ease-in-out 0.4s infinite;"></div>
            <div style="width: 16px; height: 16px; background-color: #f5222d; border-radius: 50%; animation: pulse 1.4s ease-in-out 0.6s infinite;"></div>
          </div>
          
          <!-- 信息提示卡片 -->
          <div style="margin-top: 20px; padding: 20px 30px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 12px; box-shadow: 0 8px 16px rgba(0,0,0,0.15); max-width: 600px;">
            <div style="display: flex; align-items: flex-start; gap: 12px;">
              <div style="font-size: 24px; margin-top: 2px;">💡</div>
              <div>
                <p style="margin: 0 0 8px 0; color: #ffffff; font-size: 15px; font-weight: 600;">
                  Deep Research 特色功能
                </p>
                <p style="margin: 0; color: #e6e6ff; font-size: 14px; line-height: 1.6;">
                  ✓ 搜尋並整合多個可靠來源<br/>
                  ✓ 深度分析與交叉驗證<br/>
                  ✓ 提供引用與數據支持<br/>
                  <span style="color: #ffd700; font-weight: 600;">⏱ 預計需要 20-40 秒</span>
                </p>
              </div>
            </div>
          </div>
          
          <!-- CSS 动画 -->
          <style>
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            
            @keyframes pulse {
              0%, 100% {
                transform: scale(1);
                opacity: 1;
              }
              50% {
                transform: scale(1.5);
                opacity: 0.5;
              }
            }
          </style>
        </div>
      `;
      setResult(dateHeader + loadingUI);
    } else {
      // 一般模型只显示日期头部
      setResult(dateHeader);
    }

      // 針對 AI 模型的 system prompt - 要求 Markdown 格式輸出
      const systemContent = "你是一個專業的股票分析師，請根據提供的股票代碼進行分析。請用繁體中文回答，並提供結構化的分析。重要：你的回覆必須使用 **Markdown 格式**，不要使用 HTML。請用 **粗體** 標示重點、重要數字與關鍵數據（例如：營收、EPS、漲跌幅、目標價等）。";
      
      const messages = [
        {
          role: "system",
          content: systemContent
        },
        {
          role: "user",
          content: prompt + ' 必要時加上 Markdown 表格。請用 **Markdown 格式** 回覆，使用標題（# ## ###）、有序/無序列表、**粗體** 標示重點與重要數字。'
        }
      ];

    const response = await callAIProxyStream(messages, moduleName);

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let isDone = false;
    let rawAccumulated = ''; // 累積原始內容（含 <think> 區塊）
    let hasReceivedFirstContent = false;

    // 過濾全字串中的 <think> 區塊（跨 chunk 安全）
    const filterThink = (str) =>
      str
        .replace(/<think>[\s\S]*?<\/think>/g, '') // 移除已閉合的 think 區塊
        .replace(/<think>[\s\S]*$/, '');           // 移除尚未閉合的 think 區塊
    
    while (!isDone) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value);
      buffer += chunk;
      
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmedLine = line.trim();
        
        // 跳过空行
        if (trimmedLine === '') continue;
        
        // 跳过 SSE 注释行（以 : 开头的心跳包）
        if (trimmedLine.startsWith(':')) continue;
        
        // 检查是否结束
        if (trimmedLine === 'data: [DONE]') {
          isDone = true;
          break;
        }

        // 只处理以 data: 开头的行
        if (!trimmedLine.startsWith('data:')) continue;

        try {
          const jsonStr = trimmedLine.substring(5).trim(); // 移除 'data:' 前缀
          if (!jsonStr) continue;
          
          const json = JSON.parse(jsonStr);
          if (json.error) throw new Error(json.error);
          const content = json.choices?.[0]?.delta?.content;
          if (content) {
            // 累積原始 token（不先過濾，讓跨 chunk 的 <think> 能被完整偵測）
            rawAccumulated += content;

            // 對完整累積字串做 <think> 過濾
            const filtered = filterThink(rawAccumulated);

            if (!filtered.trim()) continue;

            if (!hasReceivedFirstContent) {
              hasReceivedFirstContent = true;
              logger.info('Deep Research 開始返回內容');
            }
            
            // 提取 ```markdown 或 ```md 代碼塊，若無則使用原始內容
            let markdownContent = filtered;
            const mdBlockMatch = filtered.match(/```(?:markdown|md)?\s*([\s\S]*?)(?:```|$)/);
            if (mdBlockMatch) {
              markdownContent = mdBlockMatch[1].trim();
            }
            
            // 將 Markdown 轉為 HTML 並顯示
            const displayHtml = convertMarkdownToHtml(markdownContent);
            setResult(dateHeader + displayHtml);
          }
        } catch (e) {
          logger.warn('解析串流數據時出錯:', e, '原始行:', trimmedLine);
        }
      }
    }

    // 串流結束，計算最終過濾後的內容（供快取使用）
    const aggregated = filterThink(rawAccumulated);

    // 串流結束後存入永久緩存（包含日期頭部和轉換後的 HTML 內容）
    const cacheKey = makeCacheKey(analysisType, symbol);
    if (aggregated && cacheKey) {
      // 提取 ```markdown 或 ```md 代碼塊，若無則使用原始內容
      let markdownToConvert = aggregated;
      const mdBlockMatch = aggregated.match(/```(?:markdown|md)?\s*([\s\S]*?)(?:```|$)/);
      if (mdBlockMatch) {
        markdownToConvert = mdBlockMatch[1].trim();
      }
      
      // 將 Markdown 轉為 HTML 後保存
      const contentToSave = convertMarkdownToHtml(markdownToConvert);
      
      if (DEBUG_MODE) {
        console.log('='.repeat(80));
        console.log(`📄 ${analysisType} 原始完整内容 (${symbol}):`);
        console.log('='.repeat(80));
        console.log(aggregated);
        console.log('='.repeat(80));
        console.log(`📊 完整内容统计: ${aggregated.length} 字符`);
        console.log('='.repeat(80));
      }
      
      const fullContent = dateHeader + contentToSave;
      savePermanentCache(cacheKey, fullContent);
    }

    setLoadingState(false);
    if (setIsAnalyzing) {
      setIsAnalyzing(false);
    }
    activityTracker.trackBtnExecution(`${analysisType}_analysis`, symbol, `${analysisType}_analysis`);

  } catch (error) {
    logger.error(`${analysisType} 分析時發生錯誤:`, error);
    message.error(`分析失敗，請稍後再試: ${error.message}`);
    setLoadingState(false);
    if (setIsAnalyzing) {
      setIsAnalyzing(false);
    }
  }
};

/**
 * 綜合分析
 */
export const handleFundamentalAnalysis = async (
  symbol,
  mainData,
  setFundamentalAnalysis,
  setClickingFundamental,
  setIsAnalyzing
) => {
  if (!symbol) {
    message.warning('請先選擇公司');
    if (setIsAnalyzing) setIsAnalyzing(false);
    return;
  }

  setClickingFundamental(true);
  setFundamentalAnalysis('');

  const selectedCompanyName = mainData?.company_name || symbol;

  // 構建 prompt
  const prompt = `請分析美股 ${selectedCompanyName} 的基本面財務狀況，從以下角度進行分析。請用 **Markdown 格式** 輸出，並用 **粗體** 標示重點與重要數字（如營收、EPS、市佔率、估值等）：

1. 核心產品與服務介紹和最新一季的業績表現
2. 近期利多與競爭優勢：列出近期利多消息，和與主要競爭對手的商業模式、市場定位有什麼不同？有哪些優勢、護城河是什麼？
3. 近期利空與風險分析：列出近期利空消息，和各項風險因子的分析
4. 公司發展前景、未來產品發展規劃、最新一季財報會議對未來指引預期
5. 從護城河的角度和估值，值得長期投資嗎？

請按照上述結構進行詳細分析。`;

  await callPerplexityAPI(
    prompt, 
    setFundamentalAnalysis, 
    setClickingFundamental, 
    'fundamental_analysis', 
    symbol,
    setIsAnalyzing,
    'maininfo_fundamental'  // 使用 Deep Research 模型
  );
};

/**
 * 產品業務分析
 */
export const handleProductBusinessAnalysis = async (
  symbol,
  mainData,
  setProductBusinessAnalysis,
  setClickingProductBusiness,
  setIsAnalyzing
) => {
  if (!symbol) {
    message.warning('請先選擇公司');
    if (setIsAnalyzing) setIsAnalyzing(false);
    return;
  }

  setClickingProductBusiness(true);
  setProductBusinessAnalysis('');

  const selectedCompanyName = mainData?.company_name || symbol;

  // 構建 prompt
  const prompt = `請根據最新10-K報告分析美股 ${selectedCompanyName} 的產品業務信息。沒有10-K就從網路上收集資料分析。請用 **Markdown 格式** 輸出，並用 **粗體** 標示重點與重要數字（如營收佔比、各區業務比例等）：

1. 核心產品與服務介紹
2. 各產品業務線的營收佔比
3. 各國/地區的業務佔比
4. 產品組合策略
5. 市場定位與競爭優勢
6. 未來產品發展規劃

請按照上述結構進行詳細分析。`;

  await callPerplexityAPI(
    prompt, 
    setProductBusinessAnalysis, 
    setClickingProductBusiness, 
    'product_business', 
    symbol,
    setIsAnalyzing,
    'maininfo_other'  // 使用一般 Perplexity 模型
  );
};

/**
 * 商業模式與競爭分析
 */
/**
 * 波特五力分析
 */
export const handlePorterFiveForcesAnalysis = async (
  symbol,
  mainData,
  setPorterFiveForcesAnalysis,
  setClickingPorterFiveForces,
  setIsAnalyzing
) => {
  if (!symbol) {
    message.warning('請先選擇公司');
    if (setIsAnalyzing) setIsAnalyzing(false);
    return;
  }

  setClickingPorterFiveForces(true);
  setPorterFiveForcesAnalysis('');

  const selectedCompanyName = mainData?.company_name || symbol;

  const prompt = `請對美股 ${selectedCompanyName} 進行波特五力分析。請用 **Markdown 格式** 輸出，並用 **粗體** 標示重點與評分數字：

1. 供應商議價能力：分析供應商的集中度、轉換成本、替代品威脅
2. 買方議價能力：分析客戶的集中度、轉換成本、價格敏感度
3. 新進入者威脅：分析進入壁壘、規模經濟、品牌忠誠度
4. 替代品威脅：分析替代品的性能、價格、客戶轉換意願
5. 同業競爭程度：分析競爭者數量、市場增長率、退出壁壘

請為每個力量評分（1-5分，5分為最強），並提供具體的分析依據。`;

  await callPerplexityAPI(
    prompt, 
    setPorterFiveForcesAnalysis, 
    setClickingPorterFiveForces, 
    'porter_five_forces', 
    symbol,
    setIsAnalyzing,
    'maininfo_other'  // 使用一般 Perplexity 模型
  );
};

/**
 * 高管能力持股分析
 */
export const handleExecutiveAnalysis = async (
  symbol,
  mainData,
  setExecutiveAnalysis,
  setClickingExecutive,
  setIsAnalyzing
) => {
  if (!symbol) {
    message.warning('請先選擇公司');
    if (setIsAnalyzing) setIsAnalyzing(false);
    return;
  }

  setClickingExecutive(true);
  setExecutiveAnalysis('');

  const selectedCompanyName = mainData?.company_name || symbol;

  const prompt = `請分析美股 ${selectedCompanyName} 的高管能力與持股狀況。請用 **Markdown 格式** 輸出，並用 **粗體** 標示重點與重要數字（持股比例、薪酬、交易金額等）：

1. CEO 能力背景分析：教育背景、工作經驗、領導風格、過往業績
2. 高管團隊組成：核心管理層的專業背景、行業經驗、任期長短
3. 誠信問題評估：是否有法律糾紛、財務醜聞、內幕交易等問題
4. 持股狀況分析：股權激勵計劃、最近交易記錄、高管持股比例、以及 CEO、CFO、CTO 過去一年買賣股票的細節【一定要提到這一點】
5. 薪酬結構評估：基本薪資、獎金、股權激勵的合理性
6. 治理結構評價：董事會獨立性、審計委員會、風險管理機制

請提供具體的數據和案例支持分析結論。`;

  await callPerplexityAPI(
    prompt, 
    setExecutiveAnalysis, 
    setClickingExecutive, 
    'executive_analysis', 
    symbol,
    setIsAnalyzing,
    'maininfo_other'  // 使用一般 Perplexity 模型
  );
};

/**
 * 最新会议逐字稿分析
 * 先检查逐字稿是否存在，如果存在则通过WebSocket调用analyzeTranscriptWebsocket
 * 否则直接调用Perplexity API
 */
export const handleLatestTranscriptAnalysis = async (
  symbol,
  mainData,
  setTranscriptAnalysis,
  setClickingTranscript,
  setIsAnalyzing
) => {
  if (!symbol) {
    message.warning('請先選擇公司');
    if (setIsAnalyzing) setIsAnalyzing(false);
    return;
  }

  setClickingTranscript(true);
  setTranscriptAnalysis('');

  try {
    // 检查逐字稿是否存在
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/maininfo/check-latest-transcript/${symbol}`);
    const result = await response.json();

    if (result.success && result.hasTranscript && result.data) {
      // 存在逐字稿，使用WebSocket调用analyzeTranscriptWebsocket
      logger.info('找到最新逐字稿，使用WebSocket分析');
      await analyzeTranscriptViaWebSocket(
        result.data.symbol,
        result.data.fiscalYear,
        result.data.quarter,
        setTranscriptAnalysis
      );
    } else {
      // 不存在逐字稿，调用Perplexity API
      logger.info('未找到最新逐字稿，使用Perplexity API');
      const selectedCompanyName = mainData?.company_name || symbol;
      
      // 优化 prompt，明确要求搜索最新的财报电话会议内容
      const prompt = `請深度搜尋並分析美股 ${selectedCompanyName} (${symbol}) 最新一季的財報電話會議（Earnings Call Transcript），並提供以下內容。請用 **Markdown 格式** 輸出，並用 **粗體** 標示重點與重要數字（營收、EPS、指引等）：

1. **會議基本信息**：財報季度、會議日期、財報發布時間
2. **管理層重點發言摘要**：CEO/CFO 的主要發言重點、關鍵業績數據、各業務部門的表現
3. **業績亮點與成就**：超出預期的指標、重要產品或服務的進展、市場份額變化
4. **風險因素與挑戰**：管理層提到的風險、市場競爭壓力、行業逆風
5. **未來展望與指引**：下一季度的業績預期、長期戰略規劃、新產品或服務計劃
6. **問答環節重點**：分析師最關注的問題、管理層的重要回應

請務必搜尋並引用最新的實際會議記錄內容，必要時用 Markdown 表格展示數據。`;

      await callPerplexityAPI(
        prompt,
        setTranscriptAnalysis,
        setClickingTranscript,
        'latest_transcript',
        symbol,
        setIsAnalyzing,
        'maininfo_fundamental'  // 改用 Deep Research 模型
      );
    }

    setClickingTranscript(false);
    if (setIsAnalyzing) {
      setIsAnalyzing(false);
    }
  } catch (error) {
    logger.error('最新会议逐字稿分析失败:', error);
    message.error('分析失敗，請稍後再試');
    setClickingTranscript(false);
    if (setIsAnalyzing) {
      setIsAnalyzing(false);
    }
  }
};

/**
 * 通过WebSocket分析逐字稿
 */
const analyzeTranscriptViaWebSocket = async (symbol, fiscalYear, quarter, setTranscriptAnalysis) => {
  return new Promise((resolve, reject) => {
    try {
      // 构建WebSocket URL（與 App_transcript.js 一致：production 時 REACT_APP_API_URL 可能為空，改用 window.location）
      const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const wsHost = process.env.REACT_APP_API_URL
        ? process.env.REACT_APP_API_URL.replace(/^https?:\/\//, '')
        : (window.location.hostname === 'localhost' ? 'localhost:5080' : window.location.host);
      const wsUrl = `${wsProtocol}://${wsHost}/ws-app`;
      
      logger.info('连接WebSocket:', wsUrl);
      const ws = new WebSocket(wsUrl);

      let executiveMarkdown = '';  // 累积原始 markdown
      let qaMarkdown = '';         // 累积原始 markdown
      let hasError = false;

      // 添加当前日期到结果开头
      const currentDate = new Date().toLocaleDateString('zh-TW', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      const dateHeader = `<div style="text-align: right; color: #666; font-size: 14px; margin-bottom: 20px;">分析日期：${currentDate}</div>\n\n`;

      ws.onopen = () => {
        logger.info('WebSocket已连接，发送分析请求');
        // 发送分析请求
        ws.send(JSON.stringify({
          type: 'transcript-analysis',
          symbol,
          fiscalYear,
          quarter
        }));
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.error) {
            hasError = true;
            message.error(`分析錯誤: ${data.error}`);
            reject(new Error(data.error));
            ws.close();
            return;
          }

          if (data.type === 'executive') {
            // 高管陈述部分 - 累积原始 markdown
            executiveMarkdown += data.content || '';
            
            // 转换为 HTML
            const executiveHeader = '<h3 style="color: #1890ff; border-bottom: 2px solid #1890ff; padding-bottom: 8px;">高管陳述分析</h3>\n\n';
            const executiveHtml = convertMarkdownToHtml(executiveMarkdown);
            const qaHtml = qaMarkdown ? convertMarkdownToHtml(qaMarkdown) : '';
            const qaHeader = qaMarkdown ? '\n\n<h3 style="color: #52c41a; border-bottom: 2px solid #52c41a; padding-bottom: 8px; margin-top: 30px;">問答環節分析</h3>\n\n' : '';
            
            setTranscriptAnalysis(dateHeader + executiveHeader + executiveHtml + qaHeader + qaHtml);
          } else if (data.type === 'qa') {
            // 问答环节部分 - 累积原始 markdown
            qaMarkdown += data.content || '';
            
            // 转换为 HTML
            const executiveHeader = '<h3 style="color: #1890ff; border-bottom: 2px solid #1890ff; padding-bottom: 8px;">高管陳述分析</h3>\n\n';
            const executiveHtml = executiveMarkdown ? convertMarkdownToHtml(executiveMarkdown) : '';
            const qaHeader = '\n\n<h3 style="color: #52c41a; border-bottom: 2px solid #52c41a; padding-bottom: 8px; margin-top: 30px;">問答環節分析</h3>\n\n';
            const qaHtml = convertMarkdownToHtml(qaMarkdown);
            
            setTranscriptAnalysis(dateHeader + executiveHeader + executiveHtml + qaHeader + qaHtml);
          } else if (data.type === 'complete') {
            logger.info('逐字稿分析完成');
            
            // 最终转换并保存到缓存
            const executiveHeader = executiveMarkdown ? '<h3 style="color: #1890ff; border-bottom: 2px solid #1890ff; padding-bottom: 8px;">高管陳述分析</h3>\n\n' : '';
            const executiveHtml = executiveMarkdown ? convertMarkdownToHtml(executiveMarkdown) : '';
            const qaHeader = qaMarkdown ? '\n\n<h3 style="color: #52c41a; border-bottom: 2px solid #52c41a; padding-bottom: 8px; margin-top: 30px;">問答環節分析</h3>\n\n' : '';
            const qaHtml = qaMarkdown ? convertMarkdownToHtml(qaMarkdown) : '';
            
            const finalHtml = dateHeader + executiveHeader + executiveHtml + qaHeader + qaHtml;
            
            const cacheKey = makeCacheKey('latest_transcript', symbol);
            if (cacheKey) {
              savePermanentCache(cacheKey, finalHtml);
            }
            
            // 记录活动
            activityTracker.trackBtnExecution('latest_transcript_analysis', symbol, 'latest_transcript_analysis');
            ws.close();
            resolve();
          }
        } catch (error) {
          logger.error('处理WebSocket消息时出错:', error);
          if (!hasError) {
            hasError = true;
            reject(error);
          }
        }
      };

      ws.onerror = (error) => {
        logger.error('WebSocket错误:', error);
        if (!hasError) {
          hasError = true;
          message.error('WebSocket連接錯誤');
          reject(error);
        }
      };

      ws.onclose = () => {
        logger.info('WebSocket已关闭');
        if (!hasError) {
          resolve();
        }
      };

      // 设置超时
      setTimeout(() => {
        if (ws.readyState !== WebSocket.CLOSED) {
          ws.close();
          if (!hasError) {
            hasError = true;
            reject(new Error('WebSocket连接超时'));
          }
        }
      }, 120000); // 2分钟超时
    } catch (error) {
      logger.error('创建WebSocket连接时出错:', error);
      reject(error);
    }
  });
};

/**
 * 當天市場狀況及該股消息分析
 */
export const handleDailyMarketNewsAnalysis = async (
  symbol,
  mainData,
  setDailyMarketNews,
  setClickingDailyMarketNews,
  setIsAnalyzing
) => {
  if (!symbol) {
    message.warning('請先選擇公司');
    if (setIsAnalyzing) setIsAnalyzing(false);
    return;
  }

  setClickingDailyMarketNews(true);
  setDailyMarketNews('');

  const selectedCompanyName = mainData?.company_name || symbol;
  
  const prompt = `請深度搜尋並分析今天（${new Date().toLocaleDateString('zh-TW')}）美股 ${selectedCompanyName} (${symbol}) 的市場狀況及相關消息。請用 **Markdown 格式** 輸出，並用 **粗體** 標示重點與重要數字（股價、漲跌幅、成交量等）：

1. **當天市場概況**：今日股價表現（開盤、收盤、漲跌幅）、成交量變化、技術面短評
2. **當天重要消息**：公司官方公告、產品或服務相關新聞、管理層動態
3. **市場情緒與討論**：社群媒體熱度、投資者情緒指標、主流媒體報導焦點
4. **行業動態影響**：同業表現對比、產業鏈相關消息、政策或監管變化
5. **即時事件影響**：突發事件或意外消息、盤中異動原因分析

必要時用 Markdown 表格呈現。如果今天是非交易日或暫無重要消息，請說明並提供最近一個交易日的信息。`;

  await callPerplexityAPI(
    prompt,
    setDailyMarketNews,
    setClickingDailyMarketNews,
    'daily_market_news',
    symbol,
    setIsAnalyzing,
    'maininfo_other'
  );
};

/**
 * 近期重要消息分析
 */
export const handleRecentNewsAnalysis = async (
  symbol,
  mainData,
  setRecentNews,
  setClickingRecentNews,
  setIsAnalyzing
) => {
  if (!symbol) {
    message.warning('請先選擇公司');
    if (setIsAnalyzing) setIsAnalyzing(false);
    return;
  }

  setClickingRecentNews(true);
  setRecentNews('');

  const selectedCompanyName = mainData?.company_name || symbol;
  
  const prompt = `請深度搜尋並整理美股 ${selectedCompanyName} (${symbol}) 近期（過去30天內）的重要消息，請列出最重要的十條消息，並提供簡要的分析。請用 **Markdown 格式** 輸出，並用 **粗體** 標示重點與重要數字（日期、金額、漲跌幅等）：

1. **重大公告與事件**：財報發布與業績表現、併購或合作夥伴關係、產品發布或技術突破、管理層變動
2. **業務進展**：新訂單或合約、市場拓展計劃、產能擴充或設施投資、研發成果
3. **財務動態**：融資或股票回購、股利政策變化、債務重組、資本支出計劃
4. **市場反應**：重要消息發布後的股價反應、市場對消息的解讀、交易量變化
5. **風險提示**：負面新聞或訴訟、監管調查或處罰、產品召回或質量問題

請按時間倒序排列，為每則消息標註日期，必要時用 Markdown 表格整理關鍵信息。`;

  await callPerplexityAPI(
    prompt,
    setRecentNews,
    setClickingRecentNews,
    'recent_news',
    symbol,
    setIsAnalyzing,
    'maininfo_other'
  );
};

/**
 * 分析師升降評或調整目標價理由分析
 */
export const handleAnalystRatingAnalysis = async (
  symbol,
  mainData,
  setAnalystRating,
  setClickingAnalystRating,
  setIsAnalyzing
) => {
  if (!symbol) {
    message.warning('請先選擇公司');
    if (setIsAnalyzing) setIsAnalyzing(false);
    return;
  }

  setClickingAnalystRating(true);
  setAnalystRating('');

  const selectedCompanyName = mainData?.company_name || symbol;
  
  const prompt = `請深度搜尋並分析美股 ${selectedCompanyName} (${symbol}) 近期（過去90天內）的分析師評級變動。請用 **Markdown 格式** 輸出，並用 **粗體** 標示重點與重要數字（目標價、評級、漲跌幅等）：

1. **最新評級概況**：當前綜合評級（買入/持有/賣出比例）、平均目標價與當前股價對比、目標價區間（最高/最低）、評級變動趨勢
2. **主要券商評級變動**：升評的券商及理由、降評的券商及理由、目標價調整的券商及幅度、重點關注的分析師觀點
3. **升降評詳細理由**：基本面因素、行業趨勢與競爭地位、產品週期與市場需求、估值水平評估、風險因素考量
4. **目標價調整分析**：調高/調低目標價的券商數量及平均幅度、目標價變化的主要驅動因素
5. **市場共識變化**：過去三個月評級變化趨勢、分析師信心指標、目標價達成概率、與同業評級對比
6. **投資建議總結**：主流機構的一致觀點、主要分歧點、風險提示

請用 Markdown 表格詳細列出各券商的評級變動、目標價及理由，按日期倒序排列。`;

  await callPerplexityAPI(
    prompt,
    setAnalystRating,
    setClickingAnalystRating,
    'analyst_rating',
    symbol,
    setIsAnalyzing,
    'maininfo_other'
  );
};

/**
 * 分析類型配置
 * 用於下拉選擇框和其他需要分析類型信息的地方
 */
export const ANALYSIS_TYPES = {
  fundamental: {
    key: 'fundamental',
    label: '深度綜合分析',
    cacheKey: 'fundamental_analysis',
    handler: handleFundamentalAnalysis
  },
  productBusiness: {
    key: 'productBusiness',
    label: '產品業務分析',
    cacheKey: 'product_business',
    handler: handleProductBusinessAnalysis
  },
  porterFiveForces: {
    key: 'porterFiveForces',
    label: '波特五力分析',
    cacheKey: 'porter_five_forces',
    handler: handlePorterFiveForcesAnalysis
  },
  executive: {
    key: 'executive',
    label: '高管能力持股分析',
    cacheKey: 'executive_analysis',
    handler: handleExecutiveAnalysis
  },
  latestTranscript: {
    key: 'latestTranscript',
    label: '最新會議逐字稿',
    cacheKey: 'latest_transcript',
    handler: handleLatestTranscriptAnalysis
  },
  dailyMarketNews: {
    key: 'dailyMarketNews',
    label: '當天市場狀況及該股消息',
    cacheKey: 'daily_market_news',
    handler: handleDailyMarketNewsAnalysis
  },
  recentNews: {
    key: 'recentNews',
    label: '過去三十天重要消息條列',
    cacheKey: 'recent_news',
    handler: handleRecentNewsAnalysis
  },
  analystRating: {
    key: 'analystRating',
    label: '分析師升降評或調整目標價理由',
    cacheKey: 'analyst_rating',
    handler: handleAnalystRatingAnalysis
  }
};
